/**
 * Where an imported model is in the three steps, and what the next one needs.
 *
 * An import is not one act. It reads what the filing says, then the figures it could not read
 * get corrected, then a forecast is fitted to the result — and each step depends on the one
 * before it. The software used to hide that: an import either produced a finished model or
 * refused outright, and a forecast would run happily on accounts that did not add up.
 *
 *   1. IMPORTED. What the filing reports, and nothing past it. A model appears even where the
 *      balance sheet does not balance, because refusing left nothing to correct.
 *   2. THE ACCOUNTS ADD UP. Until they do, at least one figure was read wrongly. The Overview
 *      says which figure the filing's own text disagrees with; nothing is plugged.
 *   3. FORECAST. Fitted to the history above, so it waits for step two and needs years beyond
 *      the filing to forecast into.
 *
 * It reports rather than instructs: each step shows what is true and what the next one is
 * waiting for. A step cannot be "done" here because the user did something — only because the
 * model says so.
 */

import { useFinancialModelV2Store } from '../../../store/financial-model-v2';

type State = 'done' | 'todo' | 'waiting';

function Dot({ state }: { state: State }) {
  const tone =
    state === 'done'
      ? 'bg-green-600 text-white'
      : state === 'todo'
        ? 'bg-amber-500 text-white'
        : 'bg-gray-200 text-gray-500';
  return (
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${tone}`}
      aria-hidden
    >
      {state === 'done' ? '✓' : ''}
    </span>
  );
}

function Step({
  n,
  title,
  state,
  detail,
}: {
  n: number;
  title: string;
  state: State;
  detail: string;
}) {
  return (
    <li className="flex gap-3">
      <Dot state={state} />
      <div className="min-w-0">
        <div
          className={`text-sm font-medium ${
            state === 'waiting' ? 'text-gray-500' : 'text-gray-900'
          }`}
        >
          {n}. {title}
        </div>
        <div className="text-xs text-gray-600">{detail}</div>
      </div>
    </li>
  );
}

export function ImportSteps() {
  const model = useFinancialModelV2Store((s) => s.model);
  if (!model) return null;

  // Only for a model that came from a filing. One built by hand has no import to be part of
  // the way through, and a checklist on it would be noise.
  const imported = Boolean(model.documentKept) || Boolean(model.figureFlags?.length);
  if (!imported) return null;

  const historical = model.settings.historicalYears ?? [];
  const balanceCheck = model.computed.balanceSheet.balanceCheck ?? {};
  const offending = Object.entries(balanceCheck).filter(
    ([, value]) => Math.abs(value) > 1,
  );
  const balances = offending.length === 0;

  const historicalSet = new Set(historical);
  const forecastYears = Array.from(
    { length: model.settings.numForecastYears },
    (_, index) => model.settings.forecastStartYear + index,
  ).filter((year) => !historicalSet.has(year));
  const hasForecast = Boolean(model.forecastDistribution);

  const span =
    historical.length > 0
      ? `${historical[0]}–${historical[historical.length - 1]}`
      : 'no years';

  return (
    <ol className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <Step
        n={1}
        title="Imported"
        state="done"
        detail={`${span} — the years the filing reports, and none past them.`}
      />
      <Step
        n={2}
        title="The accounts add up"
        state={balances ? 'done' : 'todo'}
        detail={
          balances
            ? 'Assets less liabilities equals equity in every period.'
            : `${offending.length} period${offending.length === 1 ? '' : 's'} do not balance, so at least one figure was read wrongly. The panel below says which figure the filing's own text disagrees with.`
        }
      />
      <Step
        n={3}
        title="Forecast"
        state={hasForecast ? 'done' : balances && forecastYears.length > 0 ? 'todo' : 'waiting'}
        detail={
          hasForecast
            ? `${forecastYears.length} year${forecastYears.length === 1 ? '' : 's'} forecast from this history.`
            : !balances
              ? 'Waiting for the accounts to add up: a forecast is fitted to this history.'
              : forecastYears.length === 0
                ? 'Add the years to forecast into, in Settings.'
                : `Ready — ${forecastYears.length} year${forecastYears.length === 1 ? '' : 's'} to forecast, under AI forecast.`
        }
      />
    </ol>
  );
}
